import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  UsersIcon,
  IdentificationIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { getAdminStats } from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Stat = ({ label, value, hint }) => (
  <div>
    <p className="text-xs uppercase tracking-widest text-gray-500">{label}</p>
    <p className="text-2xl sm:text-3xl font-bold mt-1">{value}</p>
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const QuickAction = ({ to, icon: Icon, title, desc }) => (
  <Link
    to={to}
    className="group p-5 rounded-2xl border border-gray-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] hover:border-indigo-400 dark:hover:border-indigo-500/60 hover:bg-white dark:hover:bg-white/[0.05] transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{desc}</p>
      </div>
    </div>
  </Link>
);

const TIER_LABELS = { basic: "Basic", pro: "Pro", plus: "Plus", advisor: "Advisor" };

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setStats(await getAdminStats());
      } catch {
        // best-effort — leave stats null
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byTier = stats?.users_by_tier || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Admin</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Platform overview. Use the tabs above to manage users, advisors, and plans.
        </p>
      </div>

      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          <Stat label="Total users" value={loading ? "…" : stats?.total_users ?? 0} />
          <Stat label="Active subs" value={loading ? "…" : stats?.active_subscriptions ?? 0} />
          <Stat label="Advisors" value={loading ? "…" : stats?.total_advisors ?? 0} hint={`${stats?.advisors_pending ?? 0} pending review`} />
          <Stat label="Staff" value={loading ? "…" : stats?.staff_users ?? 0} />
        </div>
      </Card>

      <Card>
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Users by tier</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          {["basic", "pro", "plus", "advisor"].map((key) => (
            <Stat key={key} label={TIER_LABELS[key]} value={loading ? "…" : byTier[key] ?? 0} />
          ))}
        </div>
      </Card>

      <div>
        <h2 className="text-lg font-bold mb-3">Manage</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <QuickAction to="/admin/users" icon={UsersIcon} title="Users" desc="Search users, change tiers, grant staff, disable accounts." />
          <QuickAction to="/admin/advisors" icon={IdentificationIcon} title="Advisors" desc="Review applications, approve or reject marketplace listings." />
          <QuickAction to="/admin/plans" icon={RectangleStackIcon} title="Plans" desc="Edit the pricing catalog: copy, price, and feature bullets." />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
